import React, { useState, useEffect, useMemo } from 'react';
import UploadDocs from 'components/Atoms/UploadDocs/UploadDocs';
import InputAmount from 'components/Atoms/Inputs/InputAmount/InputAmount';
import CircleTag from 'components/Atoms/Tags/CircleTag/CircleTag';
import { useGetTaskResponsesByTask, useGetTaskResponseComments } from 'services/task/querys';
import { Task, TaskResponse, TaskState } from 'gql/graphql';
import ButtonSubmit from 'components/Atoms/Buttons/ButtonSubmit/ButtonSubmit';
import StateTag from 'components/Atoms/Tags/StateTag/StateTag';
import IssuesTaskResponse from 'scenes/Private/components/IssuesTaskResponse/IssuesTaskResponse';
import { useCreateResponseTask } from 'services/task/mutations';
import { useSequentialFileUpload } from '@common/hooks/useSequentialFileUpload';

interface CardProps {
  occupiedUnits: number;
  setOccupiedUnits: Function;
  totalUnits: number;
  setTotalUnits: Function;
  ammountFiles: number;
  task: Task | any;
  onTotalUnitsChange: Function;
  disabledEditQuantities: boolean;
  onSubmitSuccess?: Function;
}

const CardLeaseAgreements = ({
  occupiedUnits,
  setOccupiedUnits,
  totalUnits,
  setTotalUnits,
  ammountFiles,
  task,
  onTotalUnitsChange,
  disabledEditQuantities,
  onSubmitSuccess
}: CardProps) => {

  const [response, setResponse] = useState<TaskResponse | any>();
  const [localFiles, setLocalFiles] = useState<any[]>([]);

  const { data: taskResponses, reexecute: getTaskResponses } = useGetTaskResponsesByTask({
    variables: { findResponsesByTask: { taskId: task._id } }
  });

  const { data: taskComments } = useGetTaskResponseComments({
    variables: { taskId: task._id, state: 'pending' }
  });

  const { fetching, execute: createResponseTask } = useCreateResponseTask();

  // Sequential upload queue — one file at a time to respect backend limits
  const uploadQueue = useSequentialFileUpload({
    buildPayload: (file, documentId) => ({
      _id: documentId ?? response?._id,
      task: task._id,
      key: 'lease-agreements',
      label: 'Lease agreements',
      step: 2,
      metadata: { ammountFiles, occupiedUnits },
      state: TaskState.Review,
      files: [file]
    }),
    onUploadFile: createResponseTask,
    extractCreatedId: (result: any) => result?.data?.createResponseTask?._id
  });

  // Check if there's a no_action_required rejection code for this response
  const hasNoActionRequired = taskComments?.some((comment: any) =>
    comment.taskResponse._id === response?._id &&
    comment.rejectionCode?.key === 'no_action_required'
  );

  // Determine the effective state for styling and behavior
  const effectiveState = response?.state === TaskState.Approved && hasNoActionRequired
    ? TaskState.RequestError
    : response?.state;

  useEffect(() => {
    const _response: any = taskResponses?.find((item) => item.key === 'lease-agreements');
    setResponse(_response);
  }, [taskResponses, taskComments]);

  // Sync localFiles with backend files when the queue is not running
  useEffect(() => {
    if (!uploadQueue.isProcessing) {
      setLocalFiles(response?.files?.filter((f: any) => f.state !== 'request_error') || []);
    }
  }, [response, uploadQueue.isProcessing]);

  const uploadQueueFilesWithRetry = useMemo(() => {
    return uploadQueue.files.map(f => ({
      ...f,
      onRetry: () => uploadQueue.retryFile(f.id)
    }));
  }, [uploadQueue.files, uploadQueue.updateCount]);

  const generateCircles: any = () => {
    if (!response?.files) return new Array(ammountFiles).fill('draft');

    const validFiles = response.files.filter((f: any) => f.state !== 'request_error');

    if (response.state === TaskState.Review || response.state === TaskState.Approved) {
      return new Array(ammountFiles).fill(undefined).map((_: any, index: number) =>
        validFiles[index]?.state || 'draft'
      );
    }

    if (response.state === TaskState.RequestError) {
      const rejectedFiles = response.files.filter((f: any) => f.state === 'request_error');
      const neededRejected = Math.max(0, ammountFiles - validFiles.length);
      const currentFiles = [...validFiles, ...rejectedFiles.slice(-neededRejected)];
      return new Array(ammountFiles).fill(undefined).map((_: any, index: number) =>
        currentFiles[index]?.state || 'draft'
      );
    }

    return new Array(ammountFiles).fill(undefined).map((_: any, index: number) =>
      response.files?.[index]?.state || 'draft'
    );
  };

  const handleOccupiedUnitsChange = (value: number) => {
    const newTotalUnits = Math.max(value, totalUnits);
    const newVacantUnits = newTotalUnits - value;
    setOccupiedUnits(value);
    setTotalUnits(newTotalUnits);
    onTotalUnitsChange(newTotalUnits, newVacantUnits);
  };

  const validFilesCount = response?.files?.filter((f: any) => f.state !== 'request_error')?.length ?? 0;
  const allFilesUploaded = validFilesCount >= ammountFiles;

  // Locked only when all required files are uploaded (Review) or task is approved
  const isUploadDisabled = (response?.state === TaskState.Review && allFilesUploaded) || response?.state === TaskState.Approved;

  const hasNewFiles = localFiles.some((f: any) => !f.key && !f.isTemporary);

  const onSubmitDoc = async () => {
    const newFiles = localFiles.filter((f: any) => !f.key && !f.queueId && !f.isTemporary);
    if (newFiles.length === 0) return;

    const fileStates = uploadQueue.addFiles(newFiles);
    const tempFiles = fileStates.map(fs => ({
      name: fs.file.name,
      size: fs.file.size,
      queueId: fs.id,
      isTemporary: true
    }));

    setLocalFiles(prev => [
      ...(prev?.filter((f: any) => f.key || f.isTemporary) || []),
      ...tempFiles
    ]);

    await uploadQueue.startQueue(fileStates);
    await getTaskResponses({ requestPolicy: 'network-only' });
    if (onSubmitSuccess) onSubmitSuccess();
  };

  const getStateResponse = () => {
    if (response?.state === TaskState.Approved && (response?.files?.length ?? 0) > 0) {
      return (response?.files?.filter((item: any) => item.state === 'request_error')?.length ?? 0) > 0 ?
        response?.files?.filter((item: any) => item.state === 'approved').length === ammountFiles ?
          TaskState.Approved : TaskState.RequestError : TaskState.Approved;
    } else if (response?.state === TaskState.Review) return TaskState.Review;
    else if (response?.state === TaskState.RequestError) return TaskState.RequestError;
    return TaskState.Draft;
  };

  const isSubmitDisabled = isUploadDisabled || !hasNewFiles || occupiedUnits === 0 || uploadQueue.isProcessing;

  return (
    <div className={`CardLeaseAgreements CardLeaseAgreements-${response?.state}`}>
      {response?.state && response?.state !== TaskState.Review && <StateTag state={getStateResponse()} />}
      {response?.state && response?.state === TaskState.Review && <StateTag state={getStateResponse()} isLabelLocked={allFilesUploaded} />}

      <div id={response?._id} className='mt-5'>
        <InputAmount
          title="How many units are occupied?"
          type="edit"
          text={null}
          value={ammountFiles}
          onChange={handleOccupiedUnitsChange}
          className={`InputAmount-edit ${disabledEditQuantities ? 'disabled' : ''}`}
          disabled={effectiveState === TaskState.Review ||
            effectiveState === TaskState.Approved || effectiveState === TaskState.RequestError
            || disabledEditQuantities
          }
        />
        <article className="flex-btw">
          <div>
            <p className="body-card" style={{ marginBottom: '17px' }}>
              Submit leases for each of the occupied units
            </p>
            <div className="flex">
              <p className="h4">Submitted leases for occupied units</p>
              <div className='circles-tags'>
                {generateCircles()?.map((state: string, index: number) => (
                  <CircleTag key={index} state={state} tagKey={state} />
                ))}
              </div>
            </div>
          </div>
        </article>
      </div>

      <UploadDocs
        numberOfFiles={isUploadDisabled ? 0 : ammountFiles}
        style={{ padding: '4px' }}
        listFileUpload={localFiles}
        setListFileUpload={setLocalFiles}
        uploadQueueFiles={uploadQueueFilesWithRetry}
        button={
          <ButtonSubmit
            loading={fetching || uploadQueue.isProcessing}
            modalConfirm={{ show: true }}
            onClick={onSubmitDoc}
            disabled={isSubmitDisabled}
          >
            {hasNewFiles ? 'Submit for review' : 'Add at least one lease agreement to submit'}
          </ButtonSubmit>
        }
        insertHtmlBottom={
          response?.state === TaskState.RequestError && response &&
          <IssuesTaskResponse taskId={task._id} taskResponseId={response._id} />
        }
      />
    </div>
  );
};
export default CardLeaseAgreements;
